import React from "react";
import {
  Box,
  CircularProgress,
  Typography,
  Paper,
  useTheme,
  Fade,
  Stack,
} from "@mui/material";
import { LogoImage } from "../layout/Logo";

export interface LoadingSpinnerProps {
  size?: "small" | "medium" | "large" | "xl";
  variant?: "simple" | "branded" | "detailed" | "minimal";
  message?: string;
  subMessage?: string;
  minHeight?: string | number;
  fullScreen?: boolean;
  backdrop?: boolean;
  color?: "primary" | "secondary" | "inherit";
  showLogo?: boolean;
}

const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
  size = "medium",
  variant = "simple",
  message,
  subMessage,
  minHeight = "50vh",
  fullScreen = false,
  backdrop = false,
  color = "primary",
  showLogo = false,
}) => {
  const theme = useTheme();

  // Size configurations
  const sizeConfig = {
    small: { spinner: 32, logo: 24, spacing: 1 },
    medium: { spinner: 60, logo: 40, spacing: 2 },
    large: { spinner: 80, logo: 60, spacing: 3 },
    xl: { spinner: 120, logo: 80, spacing: 4 },
  };

  const config = sizeConfig[size];

  // Render different variants
  const renderSimple = () => (
    <Fade in timeout={300}>
      <CircularProgress size={config.spinner} color={color} thickness={4} />
    </Fade>
  );

  const renderBranded = () => (
    <Fade in timeout={500}>
      <Stack spacing={config.spacing} alignItems="center">
        {showLogo && (
          <Box>
            <LogoImage height={config.logo} noText />
          </Box>
        )}

        <Box position="relative">
          {/* Outer spinning ring */}
          <CircularProgress
            size={config.spinner}
            thickness={2}
            color="secondary"
            sx={{
              position: "absolute",
            }}
          />

          {/* Inner spinning ring */}
          <CircularProgress
            size={config.spinner * 0.7}
            thickness={6}
            color={color}
            sx={{
              position: "relative",
              left: config.spinner * 0.15,
              top: config.spinner * 0.15,
            }}
          />
        </Box>

        {message && (
          <Typography
            variant={size === "small" ? "body2" : "h6"}
            color="primary"
            fontWeight="medium"
            textAlign="center"
          >
            {message}
          </Typography>
        )}

        {subMessage && (
          <Typography
            variant="body2"
            color="text.secondary"
            textAlign="center"
            sx={{ opacity: 0.8 }}
          >
            {subMessage}
          </Typography>
        )}
      </Stack>
    </Fade>
  );

  const renderDetailed = () => (
    <Fade in timeout={700}>
      <Paper
        elevation={4}
        sx={{
          p: 4,
          borderRadius: 4,
          background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.background.default} 100%)`,
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Stack spacing={config.spacing} alignItems="center">
          {showLogo && (
            <Box sx={{ mb: 2 }}>
              <LogoImage height={config.logo} noText />
            </Box>
          )}

          <Box position="relative">
            {/* Multiple spinning rings for complex effect */}
            <CircularProgress
              size={config.spinner + 20}
              thickness={1}
              color="secondary"
              sx={{
                position: "absolute",
                left: -10,
                top: -10,
                opacity: 0.3,
              }}
            />

            <CircularProgress
              size={config.spinner}
              thickness={4}
              color={color}
            />

            <CircularProgress
              size={config.spinner - 20}
              thickness={6}
              color="secondary"
              variant="determinate"
              value={75}
              sx={{
                position: "absolute",
                left: 10,
                top: 10,
                opacity: 0.6,
              }}
            />
          </Box>

          {message && (
            <Typography
              variant="h6"
              color="primary"
              fontWeight="bold"
              textAlign="center"
            >
              {message}
            </Typography>
          )}

          {subMessage && (
            <Typography
              variant="body2"
              color="text.secondary"
              textAlign="center"
              sx={{
                opacity: 0.8,
                fontStyle: "italic",
              }}
            >
              {subMessage}
            </Typography>
          )}

          {/* Simple dots */}
          <Stack direction="row" spacing={0.5} alignItems="center">
            {[0, 1, 2].map((index) => (
              <Box
                key={index}
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  backgroundColor: theme.palette.primary.main,
                  opacity: 0.7,
                }}
              />
            ))}
          </Stack>
        </Stack>
      </Paper>
    </Fade>
  );

  const renderMinimal = () => (
    <Fade in timeout={200}>
      <CircularProgress
        size={config.spinner}
        thickness={3}
        color={color}
        sx={{
          opacity: 0.8,
        }}
      />
    </Fade>
  );

  // Choose variant renderer
  const renderContent = () => {
    switch (variant) {
      case "branded":
        return renderBranded();
      case "detailed":
        return renderDetailed();
      case "minimal":
        return renderMinimal();
      default:
        return renderSimple();
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        height: "100vh",
        maxWidth: "none",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        ...(backdrop && {
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          backdropFilter: "blur(4px)",
          zIndex: theme.zIndex.modal,
        }),
      }}
    >
      {renderContent()}
    </Box>
  );
};

export default LoadingSpinner;
